import type { TPostCommentFormElement } from '@/postCommentFormElement';
import { PostCommentFormElement } from '@/postCommentFormElement';
import { PostCommentsFeedElement } from '@/postCommentsFeedElement';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/ui/DropdownMenu';
import { LoadMore } from '@/ui/LoadMore/LoadMore';
import { ArrowLeft, CaretDown } from '@phosphor-icons/react/dist/ssr';
import classNames from 'classnames';
import { Suspense, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { PostCommentsElementProps, TPostCommentsElement } from './PostComments.types';

import { computeFont } from '@/utils/fonts';
import * as styles from './PostComments.css';

const makePostCommentFormNode = (element: TPostCommentsElement, id = 'post-comment-form-element') =>
  ({
    type: 'postCommentForm',
    attrs: {
      id,
      textColor: element.attrs.commentFormTextColor,
      submitButtonText: element.attrs.commentFormSubmitButtonText,
      submittingButtonText: element.attrs.commentFormSubmittingButtonText,
      cancelButtonText: element.attrs.commentFormCancelButtonText,
      placeholderText: element.attrs.commentFormPlaceholderText,
      textareaPadding: element.attrs.commentFormTextareaPadding,
      textareaBorderColor: element.attrs.commentFormTextareaBorderColor,
      textareaBorderWidth: element.attrs.commentFormTextareaBorderWidth,
      textareaBorderRadius: element.attrs.commentFormTextareaBorderRadius,
      textareaBorderStyle: element.attrs.commentFormTextareaBorderStyle,
      textareaTextColor: element.attrs.commentFormTextareaTextColor,
      textareaPlaceholderColor: element.attrs.commentFormTextareaPlaceholderColor,
      submitButtonTextColor: element.attrs.commentFormSubmitButtonTextColor,
      submitButtonBackgroundColor: element.attrs.commentFormSubmitButtonBackgroundColor,
      cancelButtonTextColor: element.attrs.commentFormCancelButtonTextColor,
      cancelButtonBackgroundColor: element.attrs.commentFormCancelButtonBackgroundColor,
    },
    content: [
      {
        id: '',
        text: '',
      },
    ],
  }) as TPostCommentFormElement;

export function PostCommentsElement(props: PostCommentsElementProps) {
  const {
    element,
    attributes,
    isLive = false,
    onReorderComments,
    commentOrder,
    onBackToPost,
    postTitle,
    commentsOrderOptions = [
      { label: 'Newest first', value: 'desc' },
      { label: 'Oldest first', value: 'asc' },
      { label: 'Most popular', value: 'popular' },
    ],
    onLoadMoreComments,
    isLoadingMoreComments = false,
    // remainingCommentsCount,
    children,
    isSubmitting,
    areCommentsLoading,
    showActions,
    onClickAction,
    onClickLike,
    onClickLogin,
    onClickSubscribe,
    replyingToCommentId,
    setCurrentComment,
    setReplyingToCommentId,
    isVisitorLoggedIn = false,
    translations,
    avatarUrl,
    id,
    commenterId,
  } = props;

  const {
    title,
    gap,
    titleColor,
    backgroundColor,
    borderColor,
    borderStyle,
    borderRadius,
    borderWidth,
    padding,
    commentsAuthorBorderColor,

    // Comments Feed
    commentsFeedFontSize,
    commentsFeedFontFamily,
    commentsFeedFontWeight,
    commentsFeedCommentTextColor,
    commentsFeedCommenterTextColor,
    commentsFeedTimestampTextColor,
    commentsFeedActionTextColor,
    commentsFeedModeratorTextColor,

    // Comment Form
    commentFormTextColor,
    commentFormTextareaPadding,
    commentFormTextareaBorderColor,
    commentFormTextareaBorderWidth,
    commentFormTextareaBorderRadius,
    commentFormTextareaBorderStyle,
    commentFormTextareaTextColor,
    commentFormTextareaPlaceholderColor,
    commentFormSubmitButtonTextColor,
    commentFormSubmitButtonBackgroundColor,
    commentFormCancelButtonTextColor,
    commentFormCancelButtonBackgroundColor,
    commentFormDropdownBorderColor,
    commentFormDropdownBorderWidth,
    commentFormDropdownBorderRadius,
    commentFormDropdownBorderStyle,
    commentFormDropdownTextColor,
    commentFormDropdownBackgroundColor,
  } = element.attrs;

  const handleChangeInCommentsOrder = (value: string) => onReorderComments?.(value);

  const handleClickLoadMore = () => onLoadMoreComments?.();

  const canComment = props.canComment || !isLive;
  const showLoadMore = props.showLoadMore || !isLive;
  const showBackToPost = props.showBackToPost && isLive;

  const commentsOrderLabelsByValue: Record<string, string> = useMemo(() => {
    return commentsOrderOptions.reduce((pv, cv) => {
      pv[cv.value] = cv.label;
      return pv;
    }, {});
  }, [commentsOrderOptions]);

  return (
    <div
      className={classNames('relative flex flex-col w-full', !isLive && 'pointer-events-none')}
      id={id}
      style={
        {
          gap,
          backgroundColor,
          borderColor,
          borderStyle,
          borderRadius,
          borderWidth,
          padding,
          fontFamily: computeFont(commentsFeedFontFamily),
          fontWeight: commentsFeedFontWeight,
          // Comments Feed
          '--comments-feed-font-size': commentsFeedFontSize,
          '--comments-feed-font-family': computeFont(commentsFeedFontFamily),
          '--comments-feed-font-weight': commentsFeedFontWeight,
          '--comments-feed-comment-text-color': commentsFeedCommentTextColor,
          '--comments-feed-commenter-text-color': commentsFeedCommenterTextColor,
          '--comments-feed-timestamp-text-color': commentsFeedTimestampTextColor,
          '--comments-feed-action-text-color': commentsFeedActionTextColor,
          '--comments-feed-moderator-text-color': commentsFeedModeratorTextColor,
          '--comments-feed-author-border-color': commentsAuthorBorderColor,

          // Comments Form
          '--comments-form-text-color': commentFormTextColor,
          '--comments-form-dropdown-border-color': commentFormDropdownBorderColor,
          '--comments-form-dropdown-border-width': commentFormDropdownBorderWidth,
          '--comments-form-dropdown-border-radius': commentFormDropdownBorderRadius,
          '--comments-form-dropdown-border-style': commentFormDropdownBorderStyle,
          '--comments-form-dropdown-text-color': commentFormDropdownTextColor,
          '--comments-form-dropdown-background-color': commentFormDropdownBackgroundColor,
          '--comments-form-textarea-padding': commentFormTextareaPadding,
          '--comments-form-textarea-border-color': commentFormTextareaBorderColor,
          '--comments-form-textarea-border-width': commentFormTextareaBorderWidth,
          '--comments-form-textarea-border-radius': commentFormTextareaBorderRadius,
          '--comments-form-textarea-border-style': commentFormTextareaBorderStyle,
          '--comments-form-textarea-text-color': commentFormTextareaTextColor,
          '--comments-form-textarea-placeholder-color': commentFormTextareaPlaceholderColor,
          '--comments-form-submit-button-text-color': commentFormSubmitButtonTextColor,
          '--comments-form-submit-button-background-color': commentFormSubmitButtonBackgroundColor,
          '--comments-form-cancel-button-text-color': commentFormCancelButtonTextColor,
          '--comments-form-cancel-button-background-color': commentFormCancelButtonBackgroundColor,
          '--comments-form-author-border-color': commentsAuthorBorderColor,
        } as CSSProperties
      }
      {...attributes}
    >
      <div
        className="flex items-center justify-between"
        style={{
          color: titleColor,
        }}
      >
        {showBackToPost ? (
          <button type="button" className="flex items-center" onClick={onBackToPost}>
            <ArrowLeft className="w-3 h-3 mr-1" />
            <span>{postTitle}</span>
          </button>
        ) : (
          <h4 className="p-0 m-0 text-lg font-extrabold leading-7">
            {title
              ? title
              : element.attrs.data.comments.length === 1
                ? translations?.['comments.count.singular']
                : translations?.['comments.count.plural']}
          </h4>
        )}

        <Suspense>
          <DropdownMenu>
            <DropdownMenuTrigger>
              <div
                className={classNames(
                  styles.dropdownBorder,
                  styles.dropdownBackground,
                  'outline-none cursor-pointer w-fit h-fit py-2 px-3 flex flex-row gap-x-2 justify-between items-center',
                )}
              >
                <div className={classNames(styles.dropdownText, 'text-sm leading-5')}>
                  {commentOrder && commentsOrderLabelsByValue[commentOrder]}
                </div>
                <div className={classNames(styles.dropdownText, 'w-5 h-5 flex items-center justify-center')}>
                  <CaretDown size={14} />
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
              <DropdownMenuContent align="end" className="z-10">
                {commentsOrderOptions.map((option) => (
                  <DropdownMenuItem key={option.value} onClick={() => handleChangeInCommentsOrder(option.value)}>
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenuPortal>
          </DropdownMenu>
        </Suspense>
      </div>
      {canComment && (
        <PostCommentFormElement
          isLive={isLive}
          isSubmitting={isSubmitting}
          element={makePostCommentFormNode(element)}
          onCommentChange={(comment: string) => {
            setReplyingToCommentId?.(null);
            setCurrentComment?.(comment);
          }}
          isVisitorLoggedIn={isVisitorLoggedIn}
          commenterId={commenterId}
          avatarUrl={avatarUrl}
          onClickLogin={onClickLogin}
          onClickSubscribe={onClickSubscribe}
          translations={translations}
          showCancelButton={false}
        >
          {null}
        </PostCommentFormElement>
      )}
      <PostCommentsFeedElement
        replyNode={makePostCommentFormNode(element, 'post-comment-reply-form-element')}
        onLike={onClickLike}
        onCommentChange={setCurrentComment}
        replyingToCommentId={replyingToCommentId}
        onClickReply={setReplyingToCommentId}
        canReply={canComment}
        isLive={isLive}
        isSubmitting={isSubmitting}
        comments={element.attrs.data.comments}
        element={{
          type: 'postCommentsFeed',
          attrs: {
            id: '',
          },
          content: [
            {
              id: '',
              text: '',
            },
          ],
        }}
        onClickAction={onClickAction}
        showActions={showActions}
        isLoading={areCommentsLoading}
        isVisitorLoggedIn={isVisitorLoggedIn}
        onClickLogin={onClickLogin}
        onClickSubscribe={onClickSubscribe}
        translations={translations}
        avatarUrl={avatarUrl}
        commenterId={commenterId}
      >
        {null}
      </PostCommentsFeedElement>
      {showLoadMore && (
        <LoadMore
          onClick={handleClickLoadMore}
          attributes={{
            paginateButtonBorderRadius: element.attrs.commentsFeedPaginateButtonBorderRadius,
            paginateButtonBorderWidth: element.attrs.commentsFeedPaginateButtonBorderWidth,
            paginateButtonBorderStyle: element.attrs.commentsFeedPaginateButtonBorderStyle,
            paginateButtonBorderColor: element.attrs.commentsFeedPaginateButtonBorderColor,
            paginateButtonPadding: element.attrs.commentsFeedPaginateButtonPadding,
            paginateButtonBackgroundColor: element.attrs.commentsFeedPaginateButtonBackgroundColor,
            paginateButtonTextColor: element.attrs.commentsFeedPaginateButtonTextColor,
            paginateButtonHoverBackgroundColor: element.attrs.commentsFeedPaginateButtonHoverBackgroundColor,
            paginateButtonHoverTextColor: element.attrs.commentsFeedPaginateButtonHoverTextColor,
            paginateButtonShadow: element.attrs.commentsFeedPaginateButtonShadow,
            paginateButtonIconColor: element.attrs.commentsFeedPaginateButtonIconColor,
            paginateButtonIconPlacement: element.attrs.commentsFeedPaginateButtonIconPlacement,
            paginateButtonIcon: element.attrs.commentsFeedPaginateButtonIcon,
            paginateButtonText: element.attrs.commentsFeedPaginateButtonText,
            paginateButtonBusyText: element.attrs.commentsFeedPaginateButtonBusyText,
            paginationMarginTop: element.attrs.commentsFeedPaginationMarginTop,
          }}
          isBusy={isLoadingMoreComments}
          isLive={isLive}
        />
      )}
      {children}
    </div>
  );
}
